/**
 * ContractorSubmittalsRfis
 * ------------------------
 * The trade partner's side of the logs: upload and resubmit submittals the PM
 * is waiting on, raise RFIs, and read the answers that come back.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Loader2, Send, Upload, Paperclip, FileText, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PILL = {
  draft: "bg-slate-100 text-slate-600 ring-slate-300",
  submitted: "bg-amber-50 text-amber-700 ring-amber-300",
  in_review: "bg-blue-50 text-blue-700 ring-blue-300",
  approved: "bg-emerald-50 text-emerald-700 ring-emerald-300",
  approved_as_noted: "bg-emerald-50 text-emerald-700 ring-emerald-300",
  revise_and_resubmit: "bg-red-50 text-red-700 ring-red-300",
  rejected: "bg-red-50 text-red-700 ring-red-300",
  closed: "bg-slate-100 text-slate-500 ring-slate-300",
  open: "bg-amber-50 text-amber-700 ring-amber-300",
  answered: "bg-blue-50 text-blue-700 ring-blue-300",
  void: "bg-slate-100 text-slate-400 ring-slate-200",
};

const Pill = ({ status }) => (
  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide ring-1 ${PILL[status] || PILL.draft}`}>
    {(status || "").replace(/_/g, " ")}
  </span>
);

export default function ContractorSubmittalsRfis({ token, projectId, projectName, tradeName }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [submittals, setSubmittals] = useState([]);
  const [rfis, setRfis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subForm, setSubForm] = useState(null);
  const [rfiForm, setRfiForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/submittals?project_id=${projectId}`, { headers }),
        axios.get(`${API}/rfis?project_id=${projectId}`, { headers }),
      ]);
      setSubmittals(s.data?.submittals || []);
      setRfis(r.data?.rfis || []);
    } catch (e) {
      setSubmittals([]); setRfis([]);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token]);
  useEffect(() => { load(); }, [load]);

  const createSubmittal = async () => {
    if (!subForm.title.trim()) return toast.error("Give the submittal a title");
    setBusy(true);
    try {
      await axios.post(`${API}/submittals`, {
        project_id: projectId, ...subForm, responsible_company: tradeName || "",
      }, { headers });
      toast.success("Submittal sent for review");
      setSubForm(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the submittal");
    } finally { setBusy(false); }
  };

  const createRfi = async () => {
    if (!rfiForm.subject.trim() || !rfiForm.question.trim()) {
      return toast.error("A subject and a question are required");
    }
    setBusy(true);
    try {
      await axios.post(`${API}/rfis`, {
        project_id: projectId, ...rfiForm, responsible_company: tradeName || "",
      }, { headers });
      toast.success("RFI issued to the project manager");
      setRfiForm(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not issue the RFI");
    } finally { setBusy(false); }
  };

  const resubmit = async (row) => {
    try {
      await axios.post(`${API}/submittals/${row.id}/submit`, {}, { headers });
      toast.success(`${row.number} resubmitted`); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resubmit");
    }
  };

  const upload = async (kind, row, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/${kind}/${row.id}/attachments`, fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} attached`); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  if (!projectId || loading) return null;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 mt-5" data-testid="contractor-logs">
      <h3 className="text-sm font-black uppercase tracking-wider text-slate-800">Submittals &amp; RFIs</h3>
      <p className="text-xs text-slate-500 mb-4">
        Everything the project manager is waiting on for <strong>{projectName}</strong>, and the answers that came back.
      </p>

      {/* Submittals */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs font-black uppercase tracking-wider text-slate-500">Your submittals</p>
        {!subForm && (
          <Button size="sm" variant="outline" data-testid="contractor-new-submittal-btn"
            onClick={() => setSubForm({ title: "", spec_section: "", division_code: "", description: "", date_required: "" })}>
            <Plus className="w-3.5 h-3.5 mr-1" />Send a submittal
          </Button>
        )}
      </div>

      {submittals.length > 0 ? (
        <div className="divide-y divide-slate-200 border border-slate-200 rounded-lg mb-3">
          {submittals.map((row) => (
            <div key={row.id} className="px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap"
              data-testid={`contractor-submittal-${row.number}`}>
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-800">
                  <span className="font-mono text-xs text-slate-500 mr-2">{row.number}</span>{row.title}
                  {row.revision > 0 ? <span className="ml-1 text-[10px] text-slate-400">R{row.revision}</span> : null}
                </p>
                <p className="text-xs text-slate-500">
                  {row.spec_section || row.division_code || "—"}
                  {row.review_due_date ? ` · review due ${row.review_due_date}` : ""}
                  {row.is_overdue ? " · OVERDUE" : ""}
                </p>
                {row.reviewer_notes ? (
                  <p className="text-xs text-slate-600 mt-1 border-l-2 border-slate-300 pl-2">
                    Reviewer: {row.reviewer_notes}
                  </p>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <Pill status={row.status} />
                {["revise_and_resubmit", "rejected", "draft"].includes(row.status) && (
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 h-7"
                    onClick={() => resubmit(row)} data-testid={`contractor-resubmit-${row.number}`}>
                    <Send className="w-3 h-3 mr-1" />Resubmit
                  </Button>
                )}
                <label className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 cursor-pointer" title="Attach a file">
                  <Upload className="w-4 h-4" />
                  <input type="file" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) upload("submittals", row, f); e.target.value = ""; }} />
                </label>
                {(row.attachments || []).length > 0 && (
                  <a href={`${BACKEND}${row.attachments[row.attachments.length - 1].url}`} target="_blank" rel="noreferrer"
                    className="p-1.5 text-slate-400 hover:text-slate-700" title="Latest attachment">
                    <Paperclip className="w-4 h-4" />
                  </a>
                )}
                <a href={`${API}/submittals/${row.id}/pdf`} target="_blank" rel="noreferrer"
                  className="p-1.5 text-slate-400 hover:text-slate-700" title="Transmittal PDF">
                  <FileText className="w-4 h-4" />
                </a>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-400 mb-3">Nothing logged against your scope yet.</p>
      )}

      {subForm && (
        <div className="rounded-lg border border-slate-300 bg-slate-50 p-3 space-y-2 mb-4">
          <div className="grid sm:grid-cols-3 gap-2">
            <Input className="sm:col-span-2 bg-white" placeholder="Title — e.g. Door hardware product data"
              value={subForm.title} onChange={(e) => setSubForm({ ...subForm, title: e.target.value })}
              data-testid="contractor-submittal-title" />
            <Input className="bg-white" placeholder="Spec section" value={subForm.spec_section}
              onChange={(e) => setSubForm({ ...subForm, spec_section: e.target.value })} />
          </div>
          <Textarea className="bg-white" rows={2} placeholder="What is enclosed?"
            value={subForm.description} onChange={(e) => setSubForm({ ...subForm, description: e.target.value })} />
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setSubForm(null)}>Cancel</Button>
            <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={createSubmittal} disabled={busy}
              data-testid="contractor-submittal-send">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              Send for review
            </Button>
          </div>
        </div>
      )}

      {/* RFIs */}
      <div className="flex items-center justify-between gap-2 mb-2 mt-5">
        <p className="text-xs font-black uppercase tracking-wider text-slate-500">Your RFIs</p>
        {!rfiForm && (
          <Button size="sm" variant="outline" data-testid="contractor-new-rfi-btn"
            onClick={() => setRfiForm({ subject: "", question: "", location: "", drawing_reference: "", date_required: "", suggested_solution: "" })}>
            <Plus className="w-3.5 h-3.5 mr-1" />Ask a question
          </Button>
        )}
      </div>

      {rfis.length > 0 ? (
        <div className="divide-y divide-slate-200 border border-slate-200 rounded-lg">
          {rfis.map((row) => (
            <div key={row.id} className="px-3 py-2.5" data-testid={`contractor-rfi-${row.number}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm font-bold text-slate-800 min-w-0">
                  <span className="font-mono text-xs text-slate-500 mr-2">{row.number}</span>{row.subject}
                </p>
                <div className="flex items-center gap-2">
                  {row.is_overdue ? <AlertTriangle className="w-4 h-4 text-red-500" title="Past due" /> : null}
                  <Pill status={row.status} />
                  <label className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 cursor-pointer" title="Attach a file">
                    <Upload className="w-4 h-4" />
                    <input type="file" className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) upload("rfis", row, f); e.target.value = ""; }} />
                  </label>
                  <a href={`${API}/rfis/${row.id}/pdf`} target="_blank" rel="noreferrer"
                    className="p-1.5 text-slate-400 hover:text-slate-700" title="RFI PDF">
                    <FileText className="w-4 h-4" />
                  </a>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                {row.date_required ? `Response needed ${row.date_required}` : "No deadline set"}
                {row.days_open !== null ? ` · ${row.days_open} days open` : ""}
              </p>
              {row.answer ? (
                <div className="mt-1.5 rounded-md bg-emerald-50 border border-emerald-200 px-2.5 py-2">
                  <p className="text-[10px] font-black uppercase tracking-wider text-emerald-700 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />Answer · {row.answered_by}
                  </p>
                  <p className="text-xs text-slate-700 whitespace-pre-wrap mt-0.5">{row.answer}</p>
                  {row.change_order_number ? (
                    <p className="text-[11px] font-bold text-emerald-700 mt-1">
                      Change order {row.change_order_number} issued from this RFI
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-400">No questions raised yet.</p>
      )}

      {rfiForm && (
        <div className="rounded-lg border border-slate-300 bg-slate-50 p-3 space-y-2 mt-3">
          <div className="grid sm:grid-cols-3 gap-2">
            <Input className="sm:col-span-2 bg-white" placeholder="Subject — e.g. Med gas conflict with ceiling grid at Room 4"
              value={rfiForm.subject} onChange={(e) => setRfiForm({ ...rfiForm, subject: e.target.value })}
              data-testid="contractor-rfi-subject" />
            <Input className="bg-white" placeholder="Drawing ref (A-201)" value={rfiForm.drawing_reference}
              onChange={(e) => setRfiForm({ ...rfiForm, drawing_reference: e.target.value })} />
          </div>
          <Textarea className="bg-white" rows={3} placeholder="Your question"
            value={rfiForm.question} onChange={(e) => setRfiForm({ ...rfiForm, question: e.target.value })}
            data-testid="contractor-rfi-question" />
          <div className="grid sm:grid-cols-3 gap-2">
            <Input className="bg-white" placeholder="Location" value={rfiForm.location}
              onChange={(e) => setRfiForm({ ...rfiForm, location: e.target.value })} />
            <Input className="bg-white" type="date" value={rfiForm.date_required}
              onChange={(e) => setRfiForm({ ...rfiForm, date_required: e.target.value })} />
            <Input className="bg-white" placeholder="Suggested solution" value={rfiForm.suggested_solution}
              onChange={(e) => setRfiForm({ ...rfiForm, suggested_solution: e.target.value })} />
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setRfiForm(null)}>Cancel</Button>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={createRfi} disabled={busy}
              data-testid="contractor-rfi-send">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              Issue RFI
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
